import { APPX_ORG_API_URL } from '~/modules/config';
import { handleException } from '~/modules/utilities/error_utilities';
import { HttpRequestConfig, http } from '~/modules/utilities/http';
import { setupOrgRequestConfig } from '~/modules/utilities/httpUtilities';

const ENDPOINT = `${APPX_ORG_API_URL}/savedOrgs`;
const SAVED_ORG_TYPE = 'favorite';

function hasHttpResponse(e: unknown): e is { response: { status?: number } } {
    return e instanceof Error && 'response' in e && typeof e.response === 'object' && e.response !== null;
}

const httpResponseStatus = (error: unknown): number | undefined =>
    (hasHttpResponse(error) ? error.response.status : undefined);

export class SavedOrgsService {
    async getFavorites(): Promise<string[]> {
        try {
            const response = await http.get(ENDPOINT, setupOrgRequestConfig());
            const raw: unknown = response.data;
            if (typeof raw !== 'object' || raw === null || !('favorites' in raw)) {
                return [];
            }
            const favs = raw.favorites;
            return Array.isArray(favs) ? favs.filter((item): item is string => typeof item === 'string') : [];
        } catch (error) {
            // Rethrow after logging so the caller can observe the failure (orgPickerStore fires a
            // load-error analytics event, then degrades to session-only per W-22901820 AC). A caught
            // error is the only failure signal — an empty favorites list is a valid success.
            handleException(error, 'savedOrgsService.getFavorites');
            throw error;
        }
    }

    // userId is sent in the request body (same pattern as savedListingsService.addFavorite /
    // removeFavorite) because the savedOrgs write API requires it for record creation; the GET
    // uses session-cookie auth only (no userId param). The server validates that the userId in the
    // body matches the authenticated session, so this is not an IDOR vector — it mirrors an
    // established pattern already in use for listing favorites.
    async addFavorite(orgId: string, userId: string): Promise<void> {
        const request: HttpRequestConfig = setupOrgRequestConfig();
        request.data = { orgId, userId, type: SAVED_ORG_TYPE };
        try {
            await http.post(ENDPOINT, request);
        } catch (error) {
            const status = httpResponseStatus(error);
            // 409 means already favorited — desired state achieved, not an error.
            if (status === 409) {
                return;
            }
            handleException(error, 'savedOrgsService.addFavorite');
            throw error;
        }
    }

    async removeFavorite(orgId: string, userId: string): Promise<void> {
        const request: HttpRequestConfig = setupOrgRequestConfig();
        request.data = { orgId, userId, type: SAVED_ORG_TYPE };
        try {
            await http.delete(ENDPOINT, request);
        } catch (error) {
            const status = httpResponseStatus(error);
            // 404 means not favorited — desired state achieved, not an error.
            if (status === 404) {
                return;
            }
            handleException(error, 'savedOrgsService.removeFavorite');
            throw error;
        }
    }
}
